import React from "react";
import {autorun} from "mobx";

import EsriAttributionViewModel from "@arcgis/core/widgets/Attribution/AttributionViewModel";

import {AppState} from "../../../components/app/app-mobx";
import {UiState} from "../../ui-state";

import "./attribution.scss";
import {AttributionConfiguration, AttributionTextItemConfiguration} from "../../config/attribution-configuration";
import {observer} from "mobx-react";
import {Icon} from "../icon/icon";

interface AttributionProps {
    config: AttributionConfiguration;
    view: __esri.MapView | __esri.SceneView | undefined;
    onLoad?: (attributionViewModel: EsriAttributionViewModel) => any;
    appStore: AppState;
    uiStore: UiState;
}

@observer
export class Attribution extends React.Component<AttributionProps, any> {
    watchHandles: any[] = [];
    mainCopyright: string[] = [];

    constructor(props: AttributionProps) {
        super(props);
        this.state = {
            viewModel: null,
            items: null,
            attributionText: "",
            isLoading: true,
            mapLoading: true
        };
        const mainCopyrightItem = this.props.config.textItems.find((item) => item.id === "copyright");
        if (mainCopyrightItem) {
            this.mainCopyright.push(mainCopyrightItem.text);
            if(mainCopyrightItem.title) {
                this.mainCopyright.push(mainCopyrightItem.title);
            }
        }
    }

    componentDidMount(): void {
        // rerun until map view is ready and layer list view model is loaded
        autorun((reaction: any) => {
            if (this.props.uiStore.widgets.has("attribution")) {
                reaction.dispose(); // stop autorun
                return;
            }

            const {view} = this.props;
            if (!view) {
                return;
            }

            const viewModel = this.loadViewModel(view);
            if (!viewModel) {
                return;
            }

            this.watchHandles.push(
                viewModel.watch("items.length",
                    (newValue: any, oldValue: any, propertyName: string, target: any) => {
                        const {items} = target;
                        const textItems: string[] = [];
                        const pattern = RegExp("^©(| )(\\d{0,4})(|,)(|Daten:) "
                            + this.mainCopyright.join("$|^©(| )(\\d{0,4})(|,)(|Daten:) ") + "$", "ui");
                        // jeder Text nur einmal und er darf nicht mit dem copyright aus der config übereinstimmen
                        items.forEach((item: __esri.AttributionItem) => {
                            if (!textItems.includes(item.text) && !pattern.test(item.text)) {
                                textItems.push(item.text);
                            }
                        });
                        this.setState({
                            items: textItems
                        });
                    })
            );

            this.watchHandles.push(
                viewModel.watch("attributionText", (attributionText: any) => {
                    this.setState({attributionText});
                })
            );
            this.watchHandles.push(view.watch("updating", (newValue) => {
                this.setState({mapLoading: newValue});
            }));

            this.setState({
                isLoading: false,
                viewModel
            });
            this.props.uiStore.addWidget("attribution", this);

            if (this.props.onLoad) {
                // call the hook
                this.props.onLoad(viewModel);
            }
            reaction.dispose();
        });
    }

    componentWillUnmount(): void {
        // TODO remove watches
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
    }

    loadViewModel(view: __esri.MapView | __esri.SceneView): EsriAttributionViewModel {
        const config = this.props.config as __esri.AttributionProperties;
        const props = {
            view,
            ...config
        };
        return new EsriAttributionViewModel(props);
    }

    render(): JSX.Element | null {
        const {config, appStore} = this.props;
        if (!config) {
            return null;
        }

        const {items, mapLoading} = this.state;

        let layerItems;
        if (items) {
            layerItems = items.join(config.itemDelimiter);
        }

        const year = new Date().getFullYear();
        const textItems = config.textItems.map((textItem: AttributionTextItemConfiguration, index: number) => {
            let href = textItem.url;
            if(appStore.topic) {
                href = href.replace("{topic_label}", appStore.topic.label);
                href = href.replace("{topic_name}", appStore.topic.name ? appStore.topic.name : "");
                href = href.replace("{subtopic_label}", appStore.subtopic?.label ? appStore.subtopic.label : "");
                href = href.replace("{subtopic_name}", appStore.subtopic?.name ? appStore.subtopic.name : "");
            }
            return <span key={textItem.id}>
                {index === 0 &&
                    <span className="copyright-year">&copy;&nbsp;{year}</span>
                }
                <a href={href} title={(textItem.title) ? textItem.title : undefined}
                    target="_blank" rel={"noreferrer"}>{textItem.text}</a>
                {index !== config.textItems.length - 1 &&
                    config.textDelimiter
                }
            </span>;
        });

        return (
            <div className={"geo-widget geo-component geo-attribution "
                + ((mapLoading)? "geo-map-updating": "geo-map-loaded") }>
                <div className="attribution-sources">{layerItems}</div>
                <div className="attribution-text">{textItems}</div>
                <Icon name={"recycle"} className={"geo-rotating"}/>
            </div>
        );
    }

}
